'use client'

import { FC, ReactNode, useEffect, useRef } from 'react'
import useSWR from 'swr'
import { usePathname } from 'next/navigation'
import { config } from '@/config'
import { logger } from '@/lib'
import { RegistrantController } from '@/controllers'
import { useAuth } from '@/hooks/useAuth'
import { AnalyticsService } from '@/services/analytics'
import { AnalyticsSessionService } from '@/services/analyticsSession'
import { AnalyticsEventType } from '@/interfaces/analytics'

interface Props {
  children: ReactNode
}

// Session guard for app_open (CW2-648 §4.3/§4.2) + the global analytics
// listeners. Mounted inside AuthProvider; everything is wrapped — analytics
// must never throw into UI code.
export const AnalyticsProvider: FC<Props> = ({ children }) => {
  const { isAuthorized, registrantId: authRegistrantId } = useAuth()
  const pathname = usePathname()
  // same SWR key as useRegistrant, so dashboard pages share the cached request
  const { data } = useSWR(isAuthorized ? config.api.endpoints.registrant.me : null, RegistrantController.me)
  // The auth context id is set synchronously by authorize() together with the cookie, so it wins
  // over the SWR cache, which is keyed by URL only and keeps the previous registrant after a token
  // change until its next revalidation. SWR is the fallback for cold loads (SSR seeds no id).
  const registrantId: number | null = isAuthorized ? (authRegistrantId ?? data?.result?.[1]?.id ?? null) : null
  const onAuthExchangeRoute = AnalyticsSessionService.isAuthExchangeRoute(pathname)
  const registrantIdRef = useRef<number | null>(null)
  const onAuthExchangeRouteRef = useRef<boolean>(false)

  useEffect(() => {
    registrantIdRef.current = registrantId
    onAuthExchangeRouteRef.current = onAuthExchangeRoute
  }, [registrantId, onAuthExchangeRoute])

  const evaluateOpen = async () => {
    try {
      const currentRegistrantId = registrantIdRef.current
      if (!currentRegistrantId) return
      if (onAuthExchangeRouteRef.current) return

      const decision = AnalyticsSessionService.evaluateOpen(currentRegistrantId)
      // the one-shot push marker never survives an evaluation (§4.2), and it
      // must be consumed synchronously with the decision — a racing evaluation
      // (SW message vs. focus/visibilitychange) would otherwise eat it while
      // this one awaits resolveIsStandalone
      const isPushEntry = AnalyticsSessionService.consumePushEntry()
      if (!decision.shouldFire) return

      const isStandalone = await AnalyticsSessionService.resolveIsStandalone()
      const entry = AnalyticsSessionService.resolveEntry(isStandalone, isPushEntry)
      AnalyticsService.track(AnalyticsEventType.APP_OPEN, {
        isStandalone,
        entry,
        gapSeconds: decision.gapSeconds,
      })
    } catch (error) {
      logger.error(error)
    }
  }

  const markLeave = () => {
    try {
      const currentRegistrantId = registrantIdRef.current
      if (!currentRegistrantId) return
      AnalyticsSessionService.markLeave(currentRegistrantId)
    } catch (error) {
      logger.error(error)
    }
  }

  // the push one-shot marker: clients.openWindow('/participant/list?entry=push')
  // delivers it via query param (§4.2)
  useEffect(() => {
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get('entry') === 'push') {
        AnalyticsSessionService.markPushEntry()
        // one-shot: strip the param so a reload, restored tab, or bookmark of
        // this URL doesn't re-mark a push entry (§4.2)
        url.searchParams.delete('entry')
        window.history.replaceState(window.history.state, '', url.toString())
      }
    } catch (error) {
      logger.error(error)
    }
  }, [])

  // evaluate at mount / registrant change (§4.3 rules 1 and 2) and when leaving
  // an auth-exchange route for the app shell (the deferred evaluation)
  useEffect(() => {
    if (!registrantId || onAuthExchangeRoute) return
    void evaluateOpen()
  }, [registrantId, onAuthExchangeRoute])

  useEffect(() => {
    const onVisibilityChange = () => {
      try {
        if (document.visibilityState === 'hidden') {
          markLeave()
          AnalyticsService.flush()
        } else {
          void evaluateOpen()
        }
      } catch (error) {
        logger.error(error)
      }
    }
    const onPageHide = () => {
      try {
        markLeave()
        AnalyticsService.flush()
      } catch (error) {
        logger.error(error)
      }
    }
    // a desktop user switching to another window without hiding the tab must
    // not leave a stale lastSeenAt that later reads as a huge gap (§4.3)
    const onBlur = () => markLeave()
    const onFocus = () => void evaluateOpen()
    const onAppInstalled = () => AnalyticsService.track(AnalyticsEventType.A2HS_INSTALLED)
    // the focused-client path of a push-notification click delivers the
    // one-shot marker via postMessage from the service worker (§4.2)
    const onSwMessage = (event: MessageEvent) => {
      try {
        if (event.data?.type === 'PUSH_NOTIFICATION_CLICK') {
          AnalyticsSessionService.markPushEntry()
          void evaluateOpen()
        }
      } catch (error) {
        logger.error(error)
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    window.addEventListener('appinstalled', onAppInstalled)
    navigator.serviceWorker?.addEventListener('message', onSwMessage)
    // without startMessages() the container's message queue only starts on an
    // onmessage assignment — a PUSH_NOTIFICATION_CLICK posted to a
    // still-loading client would be silently dropped
    navigator.serviceWorker?.startMessages?.()

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('appinstalled', onAppInstalled)
      navigator.serviceWorker?.removeEventListener('message', onSwMessage)
    }
  }, [])

  return <>{children}</>
}
